import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { GlowCard } from "../components/ui/spotlight-card";
import { useDeviceCapabilities, getAnimationConfig } from "../hooks/useDeviceCapabilities";
import { useAnalytics, trackPerformanceMetrics } from "../hooks/useAnalytics";
import { trackPageView } from "../utils/googleAnalytics";
import { buildContactEmail, buildMailtoUrl } from "../utils/contactEmail";

// — THEME —
const BG = "bg-transparent"; // let Background component handle the color
const TEXT = "text-zinc-100";
const SUB = "text-zinc-400";

// — SAFE URL PARAM HELPERS (work in sandboxed about:srcdoc) —
const memoryParams = new Map<string, string>();
const isSandboxedUrl = () =>
    typeof window !== "undefined" &&
    (window.location.href.startsWith("about:") ||
        window.location.href.startsWith("data:"));

const getParam = (key: string) => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(key);
    if (fromUrl !== null) return fromUrl;
  } catch {}
  return memoryParams.has(key) ? (memoryParams.get(key) as string) : null;
};

const setParam = (key: string, val: string | null | undefined) => {
  if (val == null || val === "" || val === "all") memoryParams.delete(key);
  else memoryParams.set(key, val);
  try {
    if (isSandboxedUrl()) return;
    const u = new URL(window.location.href);
    if (val == null || val === "" || val === "all") u.searchParams.delete(key);
    else u.searchParams.set(key, val);
    window.history.replaceState({}, "", u.toString());
  } catch {}
};

// — DATA —
// Images only. Use placeholders now; paste your real URLs later.
export type Project = {
  id: string;
  title: string;
  blurb: string;
  caseStudy?: string;
  mediaType: "image"; // locked to image per design
  src: string; // preview image URL
  side: "left" | "right";
  aspectRatio?: "square" | "landscape" | "portrait" | "mobile"; // optional aspect ratio
  links: {
    repo?: string;
    frontendRepo?: string;
    backendRepo?: string;
    demo?: string;
  };
  tags: string[];
};

const PROJECTS: Project[] = [

  {
    id: "isl-recognition",
    title: "ISL Recognition • Python",
      aspectRatio: "landscape",
      blurb:
        "Machine learning system for Israeli Sign Language recognition using computer vision and real-time webcam processing.",
    caseStudy:
        `Developed modular Israeli Sign Language recognition system combining data collection, model training, and real-time inference capabilities. Built three integrated components: automated webcam data capture with preprocessing pipeline, scalable training scripts supporting multiple CNN architectures, and lightweight GUI for sample validation and live gesture recognition. Engineered tight feedback loop between data_collection.py and train_final.py enabling rapid dataset expansion, model retraining, and performance evaluation.

Key Features:
• Rapid Iteration - Configurable CLI parameters enable reproducible training runs with hyperparameter optimization.
• Real-time Performance - Optimized webcam processing delivers instant gesture recognition with accuracy validation.
• Smart Data Management - Streamlined workflows for dataset expansion, sample labeling, and augmentation techniques.`,
    mediaType: "image",
    src: "/images/isl-recognition.jpg", // TODO
    side: "left",
    links: {
      repo: "https://github.com/Niv-Ro/ISL-Recognition", // e.g. https://github.com/Niv-Ro/ISL-Recognition
      demo: "https://youtu.be/k442R5vOBKg", // optional
    },
    tags: ["Python", "ML", "Computer Vision"],
  },
  {
    id: "tripbuddy",
    title: "TripBuddy • JavaScript / Node.js",
      aspectRatio: "landscape",
    blurb:
        "Social travel application using React and Node.js connecting travelers for group trips and experience sharing.",
    caseStudy:
        `social travel application using React.js frontend and Node.js backend connecting travelers for group trips and experience sharing. Built community platform with user matching system, group coordination tools, and social networking features for travel enthusiasts. Implemented RESTful API with MongoDB database and Firebase authentication supporting user management, messaging system, post sharing, and photo uploads. Created responsive interface with interactive maps, traveler search functionality, and seamless community engagement. 

  Key Features:
• Smart Traveler Matching - Algorithm-based system connects users by travel preferences, destinations, and compatibility for group formation.
• Social Travel Network - Post sharing, photo uploads, destination reviews, and community interaction with engagement features.
• Group Trip Coordination - Collaborative itinerary planning with shared messaging for organized group travel.`,
    mediaType: "image",
    src: "/images/tripbuddy.jpg", // TODO: replace with your preview image URL
    side: "right",
    links: {
      // TODO: paste your repos below when ready
      frontendRepo: "https://github.com/Niv-Ro/TripBuddy_Frontend", // e.g. https://github.com/Niv-Ro/TripBuddy_Frontend
      backendRepo: "https://github.com/Niv-Ro/TripBuddy_Backend", // e.g. https://github.com/Niv-Ro/TripBuddy_Backend
      demo: "https://youtu.be/bPHdT6LLyZk", // optional
    },
    tags: ["JavaScript", "Node.js", "Express", "REST"],
  },
  {
    id: "work-shift-app.jpg",
    title: "Work Shift App • Android / Java",
      aspectRatio: "mobile",
      blurb:
        "Employee scheduling application built with mobile-first design for shift management and team coordination.",
    caseStudy:
        `Developed employee scheduling application using web technologies and Firebase backend for shift management and worker coordination. Built interface enabling managers to create schedules, assign shifts, and track hours while allowing employees to communicate directly and coordinate shift swaps. Created payroll integration with detailed payslip generation including hours breakdown and overtime calculations. Designed Firebase architecture for real-time data synchronization, user authentication, and cloud-based data persistence.
        
Key Features:
• Worker Coordination Platform - Direct employee communication system with shift swap coordination and scheduling arrangement capabilities.
• Comprehensive Payroll Integration - Detailed payslip generation with hours tracking, overtime calculations, and instant earnings reports.
• Firebase-Powered Backend - Real-time data synchronization with cloud authentication and reliable data persistence for seamless user experience.`,
    mediaType: "image",
    src: "/images/work-shift-app.jpg", // Fixed double extension
    side: "left",
    links: {
      repo: "https://github.com/Niv-Ro/Work-Shift-App", // e.g. https://github.com/Niv-Ro/Work-Shift-App
      demo: "https://youtube.com/shorts/2mFZ398Upvo",
    },
    tags: ["Android", "Java", "Mobile"],
  },
  {
    id: "compsec",
    title: "Computer Security Project • ASP.NET / MySQL",
    aspectRatio: "portrait",
    blurb:
        "Web security application demonstrating common vulnerabilities and protection mechanisms including SQL injection and XSS attacks.",
    caseStudy:
        `Developed web security demonstration application using ASP.NET and MySQL showcasing SQL injection and XSS vulnerabilities with prevention techniques. Built platform demonstrating vulnerable and secure coding practices through user authentication, parameterized queries, and input validation. Integrated text file configuration for password requirements and registration settings. Configured MySQL database with secure connection management demonstrating defensive programming techniques.

Key Features:
• Vulnerability Demonstration - SQL injection and XSS attack examples with secure remediation and prevention techniques.
• Secure Coding Practices - Implementation of parameterized queries, input validation, and output encoding.
• Configurable Security Settings - Text file-based parameter configuration for password policies and registration requirements.`,
    mediaType: "image",
    src: "/images/compsec.jpg", // TODO
    side: "right",
    links: {
      repo: "https://github.com/Niv-Ro/Project_computer_security", // e.g. https://github.com/Niv-Ro/Project_computer_security
      demo: "https://youtu.be/uVdy_sMTN-Y"
    },
    tags: ["C#", "ASP.NET", "MySQL"],
  },
  {
    id: "tetris",
    title: "Tetris Game • C#",
    aspectRatio: "landscape",
      blurb:
        "Custom Tetris game featuring eight unique block pieces including innovative plus-shaped block with dynamic spawn positioning.",
    caseStudy:
        `Developed Tetris implementation featuring eight unique block pieces including custom plus-shaped block with dynamic grid spawning capabilities. Built game engine with collision detection algorithms, rotation mechanics, and line clearing functionality for enhanced puzzle gameplay. Implemented innovative plus-block that spawns at variable grid positions, adding strategic complexity beyond traditional seven-piece Tetris. Created responsive control system with keyboard input handling for piece movement, rotation, and drop functionality.

Key Features:
• Innovative Block System - Eight pieces including custom plus-shaped block with variable spawn positioning for enhanced strategic gameplay.
• Game Engine - Collision detection algorithms, smooth rotation mechanics, and real-time line clearing with responsive performance.
• Complete Controls - Keyboard input handling for precise piece movement, rotation, and dropping with polished user experience.`,
    mediaType: "image",
    src: "/images/tetris.jpg", // TODO
    side: "left",
    links: {
      repo: "https://github.com/Niv-Ro/Tetris_Game", // e.g. https://github.com/Niv-Ro/Tetris_Game
      demo: "https://youtu.be/WH8YJ1qhi6A"
    },
    tags: ["C#", "Game"],
  },
];

// — ICONS —
const GitHubIcon = (props: any) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" {...props}>
      <path d="M12 2a10 10 0 0 0-3.162 19.49c.5.094.683-.217.683-.483 0-.237-.009-.868-.014-1.703-2.78.604-3.366-1.34-3.366-1.34-.455-1.156-1.112-1.464-1.112-1.464-.909-.62.068-.607.068-.607 1.004.07 1.532 1.032 1.532 1.032.894 1.53 2.347 1.088 2.918.833.091-.647.35-1.089.636-1.34-2.22-.252-4.555-1.11-4.555-4.944 0-1.091.39-1.985 1.03-2.684-.103-.253-.446-1.27.098-2.646 0 0 .84-.269 2.75 1.026A9.564 9.564 0 0 1 12 6.844c.85.004 1.705.115 2.503.337 1.908-1.295 2.748-1.026 2.748-1.026.546 1.376.202 2.393.1 2.646.64.699 1.029 1.593 1.029 2.684 0 3.843-2.339 4.688-4.565 4.936.359.31.678.92.678 1.856 0 1.339-.012 2.42-.012 2.75 0 .268.18.58.69.481A10 10 0 0 0 12 2Z" />
    </svg>
);
const LinkedInIcon = (props: any) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" {...props}>
      <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2ZM8.339 18.339H6.003V9.75h2.336v8.589ZM7.167 8.64a1.352 1.352 0 1 1 0-2.705 1.352 1.352 0 0 1 0 2.705ZM18 18.339h-2.333v-4.472c0-1.066-.02-2.437-1.485-2.437-1.487 0-1.714 1.161-1.714 2.361v4.548h-2.334V9.75h2.24v1.22h.031c.312-.59 1.075-1.21 2.212-1.21 2.366 0 2.804 1.558 2.804 3.584v4.995Z" />
    </svg>
);
const ExternalIcon = (props: any) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M14 3h7v7h-2V6.414l-9.293 9.293-1.414-1.414L17.586 5H14V3ZM5 5h5v2H7v10h10v-3h2v5H5V5Z" />
    </svg>
);
const MenuIcon = (props: any) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M3 6h18v2H3V6Zm0 5h18v2H3v-2Zm0 5h18v2H3v-2Z" />
    </svg>
);


// — BACKGROUND —
function Background() {
  return (
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden bg-[#050a11]">
        <div
            className="absolute inset-0 min-h-full"
            style={{
              backgroundImage: `
            radial-gradient(60rem 60rem at 15% 10%, rgba(37, 99, 235, 0.4), transparent 50%),
            radial-gradient(50rem 50rem at 85% 30%, rgba(56, 189, 248, 0.3), transparent 50%), 
            radial-gradient(70rem 70rem at 50% 80%, rgba(14, 165, 233, 0.3), transparent 50%), 
            radial-gradient(40rem 40rem at 10% 65%, rgba(147, 51, 234, 0.25), transparent 50%), 
            radial-gradient(45rem 45rem at 85% 70%, rgba(236, 72, 153, 0.2), transparent 50%), 
            radial-gradient(35rem 35rem at 60% 20%, rgba(34, 197, 94, 0.15), transparent 50%) 
          `,
            }}
        />
        <div className="absolute inset-0 min-h-full bg-gradient-to-b from-transparent via-black/10 to-black/30" />
      </div>
  );
}

// — CROSS-DEVICE CLIPBOARD UTILITY —
const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    // First try the modern Clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    
    // Fallback 1: Use execCommand with temporary textarea
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    textArea.setAttribute('readonly', '');
    textArea.style.opacity = '0';
    textArea.style.pointerEvents = 'none';
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    // For iOS Safari compatibility
    if (navigator.userAgent.match(/ipad|iphone/i)) {
      const range = document.createRange();
      range.selectNodeContents(textArea);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
      textArea.setSelectionRange(0, 999999);
    }
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    return successful;
  } catch (err) {
    console.warn('Copy to clipboard failed:', err);
    return false;
  }
};

type EmailCopyStatus = 'idle' | 'copied' | 'failed';

const emailCopyLabel = (status: EmailCopyStatus) => {
  if (status === 'copied') return '✓ Copied!';
  if (status === 'failed') return 'Copy unavailable';
  return 'Copy email';
};

// — HEADER —
function Header({ onOpenSidebar }: { onOpenSidebar: () => void }) {
  const analytics = useAnalytics();
  const [copyStatus, setCopyStatus] = useState<EmailCopyStatus>('idle');
  const copyEmail = async () => {
    const success = await copyToClipboard(buildContactEmail());
    analytics.trackEmailCopy(success);
    setCopyStatus(success ? 'copied' : 'failed');
    window.setTimeout(() => setCopyStatus('idle'), success ? 2000 : 4000);
  };

  return (
      <header
          className={`relative z-50 bg-gradient-to-br from-blue-500/10 to-transparent border-b border-blue-300/20 shadow-lg shadow-blue-900/30 backdrop-blur-md`}
      >
        <div className="w-full px-4 py-4 flex items-center justify-between">
          {/* Left side - Projects Button + Title */}
          <div className="flex items-center gap-4">
            <button
                onClick={onOpenSidebar}
                className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium hover:bg-white/10 transition border border-white/20 text-white/95 touch-manipulation"
            >
              <MenuIcon className="h-4 w-4" /> 
              <span className="hidden sm:inline">Projects</span>
            </button>
            
            {/* Title - next to Projects button on all screen sizes */}
            <motion.h1
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className={`text-lg md:text-xl lg:text-2xl font-medium tracking-tight ${TEXT} whitespace-nowrap`}
            >
              Niv Romano
            </motion.h1>
          </div>

          {/* Right side - Navigation */}
          <div className="flex items-center gap-2">
            {/* Mobile Icons - visible on mobile */}
            <div className="md:hidden flex items-center gap-2">
              <button
                  onClick={copyEmail}
                  className="inline-flex items-center justify-center rounded-full p-2 text-sm font-medium hover:bg-white/10 transition border border-white/20 text-white/95 touch-manipulation min-h-[44px] min-w-[44px]"
                  title={emailCopyLabel(copyStatus)}
                  aria-label={emailCopyLabel(copyStatus)}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm8 7 8-5H4l8 5Zm-8 7h16V8l-8 5L4 8v10Z" />
                </svg>
              </button>
              <a
                  href="https://www.linkedin.com/in/niv-romano/"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => analytics.trackExternalLink('linkedin')}
                  className="inline-flex items-center justify-center rounded-full p-2 text-sm font-medium hover:bg-white/10 transition border border-white/20 text-white/95 touch-manipulation min-h-[44px] min-w-[44px]"
                  title="LinkedIn"
              >
                <LinkedInIcon className="h-5 w-5" />
              </a>
              <a
                  href="https://github.com/Niv-Ro"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => analytics.trackExternalLink('github')}
                  className="inline-flex items-center justify-center rounded-full p-2 text-sm font-medium hover:bg-white/10 transition border border-white/20 text-white/95 touch-manipulation min-h-[44px] min-w-[44px]"
                  title="GitHub"
              >
                <GitHubIcon className="h-5 w-5" />
              </a>
            </div>

            {/* Desktop Nav Links - with text */}
            <nav className="hidden md:flex items-center gap-2">
              <button
                  onClick={copyEmail}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium hover:bg-white/10 transition border border-white/20 text-white/95 cursor-pointer z-50 relative"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm8 7 8-5H4l8 5Zm-8 7h16V8l-8 5L4 8v10Z" />
                </svg>
                {emailCopyLabel(copyStatus)}
              </button>
              <a
                  href="https://www.linkedin.com/in/niv-romano/"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => analytics.trackExternalLink('linkedin')}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium hover:bg-white/10 transition border border-white/20 text-white/95 cursor-pointer z-50 relative"
              >
                <LinkedInIcon className="h-4 w-4" /> LinkedIn
              </a>
              <a
                  href="https://github.com/Niv-Ro"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => analytics.trackExternalLink('github')}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium hover:bg-white/10 transition border border-white/20 text-white/95 cursor-pointer z-50 relative"
              >
                <GitHubIcon className="h-4 w-4" /> GitHub
              </a>
            </nav>
          </div>
        </div>
      </header>
  );
}

// — MEDIA FRAME (adaptive) —
function MediaFrame({ children, aspectRatio = "square", size = 420 }: { children: any; aspectRatio?: "square" | "landscape" | "portrait" | "mobile"; size?: number }) {
  // Responsive dimensions - original desktop sizes, appropriate mobile sizes
  const getResponsiveDimensions = () => {
    // Original desktop sizes restored while keeping mobile optimized
    const baseSize = {
      mobile: "min(85vw, 320px)",
      tablet: "min(70vw, 400px)", 
      desktop: `${size}px` // Original desktop size restored
    };

    switch (aspectRatio) {
      case "landscape":
        return {
          width: baseSize.mobile,
          maxWidth: `${size * 1.25}px`, // Slightly smaller landscape desktop size 
          aspectRatio: "4/3"
        };
      case "portrait":
        return {
          width: baseSize.mobile,
          maxWidth: `${size * 0.8}px`, // Original portrait desktop size
          aspectRatio: "3/4"
        };
      case "mobile":
        return {
          width: baseSize.mobile,
          maxWidth: `${size * 0.56}px`, // Original mobile aspect desktop size
          aspectRatio: "12/25"
        };
      default: // square
        return {
          width: baseSize.mobile,
          maxWidth: baseSize.desktop, // Original square desktop size
          aspectRatio: "1/1"
        };
    }
  };
  
  const responsiveDimensions = getResponsiveDimensions();
  
  return (
      <div 
        className="relative flex justify-center" 
        style={{
          width: responsiveDimensions.width,
          maxWidth: responsiveDimensions.maxWidth,
          minWidth: aspectRatio === "landscape" && typeof window !== "undefined" && window.innerWidth >= 1024 ? "525px" : "auto",
        }}
      >
        <div
            aria-hidden
            className="absolute -inset-2 sm:-inset-4 lg:-inset-6 rounded-[20px] bg-blue-600/15 blur-xl sm:blur-2xl lg:blur-3xl"
            style={{ filter: "blur(30px)" }}
        />
        <div
            className="relative z-10 rounded-[12px] sm:rounded-[16px] lg:rounded-[20px] p-[4px] sm:p-[6px] lg:p-[8px] bg-gradient-to-br from-blue-500/10 to-transparent border border-white/5"
        >
          <GlowCard 
            customSize={true}
            glowColor="blue"
            className="h-full p-0 bg-transparent"
          >
            <div 
              className="relative rounded-[8px] sm:rounded-[12px] lg:rounded-[16px] overflow-hidden bg-black/80 border border-white/5 shadow-lg sm:shadow-xl lg:shadow-2xl shadow-black/40"
              style={{
                aspectRatio: responsiveDimensions.aspectRatio
              }}
            >
              <div className="pointer-events-none absolute inset-0 rounded-[8px] sm:rounded-[12px] lg:rounded-[16px] bg-gradient-to-b from-white/3 to-transparent" />
              {children}
            </div>
          </GlowCard>
        </div>
      </div>
  );
}

// — MEDIA (images only; blur-up) —
function MediaImage({ src, isFirst = false }: { mediaType?: "image"; src: string; isFirst?: boolean }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [isMobile, setIsMobile] = useState(true);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    // For first image, handle mobile vs desktop loading
    if (isFirst) {
      if (isMobile) {
        // On mobile, start as loaded for instant display
        setLoaded(true);
        setError(false);
      }
      
      // Always preload for performance
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
      
      // Clean up
      return () => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      };
    }
  }, [isFirst, src, isMobile]);

  const handleLoad = () => {
    setLoaded(true);
    setError(false);
  };

  const handleError = () => {
    setError(true);
    setLoaded(true); // Remove blur even on error
  };

  if (error) {
    return (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500/10 to-purple-500/10">
          <div className="text-center p-8">
            <svg className="w-16 h-16 mx-auto mb-4 text-blue-300/50" fill="currentColor" viewBox="0 0 24 24">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
            </svg>
            <p className="text-sm text-blue-200/70">Project Image</p>
          </div>
        </div>
    );
  }

  // For first image on mobile, show immediately; on desktop, allow loading states
  const showImmediately = isFirst && isMobile;
  const shouldShowLoader = !loaded && !error && !showImmediately;
  
  return (
      <>
        {shouldShowLoader && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-500/5 to-purple-500/5">
            <div className="w-8 h-8 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin"></div>
          </div>
        )}
        <img
            src={src}
            alt="Project media"
            className={`w-full h-full object-cover ${showImmediately ? '' : `transition-all ${isFirst ? 'duration-300' : 'duration-500'} ease-out`} ${loaded || showImmediately ? "blur-0 scale-100 opacity-100" : "blur-sm scale-[1.01] opacity-80"}`}
            loading={isFirst ? "eager" : "lazy"} // Prioritize first image
            {...(isFirst && { fetchPriority: "high" as any })} // High priority for first image on all devices
            onLoad={handleLoad}
            onError={handleError}
            decoding={isFirst ? "sync" : "async"} // Synchronous decoding for first image
            style={showImmediately ? {} : {
              transition: `all ${isFirst ? '300ms' : '500ms'} cubic-bezier(0.25, 0.46, 0.45, 0.94)`
            }}
        />
      </>
  );
}

// — OVERLINE —
function Overline({ children }: { children: any }) {
  return <div className="text-xs tracking-widest uppercase text-blue-200/70">{children}</div>;
}

// — CODE BUTTON (single repo or split FE/BE) —
function CodeButton({ project }: { project: Project }) {
  const [open, setOpen] = useState(false);
  const analytics = useAnalytics();
  const hasFrontend = Boolean(project?.links?.frontendRepo);
  const hasBackend = Boolean(project?.links?.backendRepo);
  const hasSplit = hasFrontend && hasBackend;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest?.("#codebtn-" + project.id);
      if (!target) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("click", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("click", onClick);
    };
  }, [open, project?.id]);

  if (!hasSplit) {
    const href = project?.links?.repo || "";
    const disabled = !href;

    if (disabled) {
      return (
          <button
              disabled
              className="inline-flex items-center gap-2 rounded-full px-4 py-3 sm:py-2 text-sm font-medium transition border border-white/10 text-zinc-500 cursor-not-allowed w-full sm:w-auto justify-center"
          >
            Code (add link)
          </button>
      );
    }

    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            onClick={() => analytics.trackExternalLink('github', project.id)}
            className="inline-flex items-center gap-2 rounded-full px-4 py-3 sm:py-2 text-sm font-medium transition border border-white/10 hover:bg-white/5 touch-manipulation w-full sm:w-auto justify-center"
        >
          Code
        </a>
    );
  }
  return (
      <div className="relative w-full sm:w-auto" id={"codebtn-" + project.id}>
        <button
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-full px-4 py-3 sm:py-2 text-sm font-medium transition border border-white/10 hover:bg-white/5 touch-manipulation w-full sm:w-auto justify-center"
        >
          Code ▾
        </button>
        {open && (
            <div className="absolute left-0 sm:left-auto right-0 sm:right-auto mt-2 w-full sm:w-44 rounded-xl border border-white/10 bg-[#0b1220] shadow-xl p-1 z-50">
              <a
                  href={project.links.frontendRepo}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => analytics.trackExternalLink('github', project.id)}
                  className={`block w-full text-center sm:text-left px-3 py-3 sm:py-2 rounded-lg touch-manipulation ${
                      hasFrontend ? "hover:bg-white/5" : "opacity-50 pointer-events-none"
                  }`}
              >
                Frontend
              </a>
              <a
                  href={project.links.backendRepo}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => analytics.trackExternalLink('github', project.id)}
                  className={`block w-full text-center sm:text-left px-3 py-3 sm:py-2 rounded-lg touch-manipulation ${
                      hasBackend ? "hover:bg-white/5" : "opacity-50 pointer-events-none"
                  }`}
              >
                Backend
              </a>
            </div>
        )}
      </div>
  );
}

// — CASE STUDY MODAL —
function CaseStudyModal({
                          project,
                          onClose,
                          onPrev,
                          onNext,
                        }: {
  project: Project | undefined;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const analytics = useAnalytics();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);
  
  if (!project) return null;
  
  return (
      <div className="fixed inset-0 z-[60]">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <div className="absolute inset-0 flex items-center justify-center p-2 sm:p-4">
          <div 
              className="relative w-full max-w-6xl h-[95vh] sm:h-auto max-h-[90vh] rounded-xl sm:rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
              style={{
                background: `
                  radial-gradient(30rem 30rem at 80% 20%, rgba(125, 211, 252, 0.2), transparent 70%), 
                  radial-gradient(35rem 35rem at 20% 70%, rgba(134, 239, 172, 0.18), transparent 70%), 
                  radial-gradient(25rem 25rem at 90% 80%, rgba(196, 181, 253, 0.15), transparent 70%), 
                  radial-gradient(28rem 28rem at 10% 30%, rgba(147, 197, 253, 0.16), transparent 70%), 
                  radial-gradient(20rem 20rem at 70% 10%, rgba(165, 243, 252, 0.14), transparent 70%), 
                  radial-gradient(32rem 32rem at 40% 90%, rgba(187, 247, 208, 0.12), transparent 70%), 
                  #0b1220
                `
              }}
          >
            <button
                onClick={onClose}
                className="absolute top-3 right-3 z-10 rounded-full px-4 py-2 sm:px-3 sm:py-1 text-sm bg-white/10 hover:bg-white/15 touch-manipulation"
            >
              <span className="hidden sm:inline">Close</span>
              <span className="sm:hidden">✕</span>
            </button>
            
            <div className="h-full overflow-y-auto">
              <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 sm:gap-6 p-4 sm:p-6 min-h-full lg:min-h-0">
                {/* Image container - centered like original */}
                <div className="flex justify-center items-center pt-8 lg:pt-0">
                  <MediaFrame aspectRatio={project.aspectRatio || "landscape"} size={380}>
                    <MediaImage src={project.src} />
                  </MediaFrame>
                </div>
                
                {/* Content */}
                <div className="flex flex-col">
                  <Overline>Case Study</Overline>
                  <h3 className="text-xl sm:text-2xl font-semibold mt-2">{project.title}</h3>
                  <p className={`${SUB} mt-3 text-sm sm:text-base whitespace-pre-line flex-1 overflow-y-auto`}>
                    {project.caseStudy || project.blurb}
                  </p>
                  
                  <div className="mt-4 flex flex-wrap gap-2">
                    {project.tags?.map((t) => (
                        <span
                            key={t}
                            className="px-3 py-1 rounded-full text-xs border border-blue-300/20 text-blue-200/90 bg-blue-500/5"
                        >
                      {t}
                    </span>
                    ))}
                  </div>
                  
                  {/* Action buttons - mobile optimized */}
                  <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <CodeButton project={project} />
                    {project.links?.demo ? (
                        <a
                            href={project.links.demo}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => analytics.trackExternalLink('demo', project.id)}
                            className="inline-flex items-center gap-2 rounded-full px-4 py-3 sm:py-2 text-sm border border-white/10 hover:bg-white/5 touch-manipulation justify-center"
                        >
                          <ExternalIcon className="h-4 w-4" /> Demo
                        </a>
                    ) : null}
                  </div>
                  
                  {/* Navigation buttons - mobile optimized */}
                  <div className="mt-6 flex justify-center sm:justify-start gap-3 pb-4 sm:pb-0">
                    <button 
                      onClick={onPrev} 
                      className="flex-1 sm:flex-none px-4 py-3 sm:py-2 rounded-full border border-white/10 hover:bg-white/5 touch-manipulation text-center"
                    >
                      ← Prev
                    </button>
                    <button 
                      onClick={onNext} 
                      className="flex-1 sm:flex-none px-4 py-3 sm:py-2 rounded-full border border-white/10 hover:bg-white/5 touch-manipulation text-center"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}

// — PROJECT SECTION —
function ProjectSection({ project, openModal, isFirst = false }: { project: Project; openModal: (id: string) => void; isFirst?: boolean }) {
  const capabilities = useDeviceCapabilities();
  const animConfig = getAnimationConfig(capabilities);
  const analytics = useAnalytics();
  const getInitialScreenSize = () => {
    if (typeof window === 'undefined') return { isLargeScreen: false };
    const width = window.innerWidth;
    return {
      isLargeScreen: width >= 1024
    };
  };
  
  const [isLargeScreen, setIsLargeScreen] = useState(() => getInitialScreenSize().isLargeScreen);
  const sideClass = project.side === "right" ? "lg:flex-row-reverse" : "lg:flex-row";
  const mediaInitialX = project.side === "right" ? 24 : -24;
  const contentInitialX = project.side === "right" ? -24 : 24;

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsLargeScreen(width >= 1024);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Calculate optimized tilt values based on device capabilities
  const getTiltConfig = () => {
    if (!animConfig.enabled || !animConfig.complex || !isLargeScreen) {
      return { rotate: 0, translateX: 0, translateY: 0 };
    }
    
    const baseRotate = project.side === "right" ? 6 : -6;
    const baseTranslateX = project.side === "right" ? 15 : -15;
    const baseTranslateY = -10;
    
    // Reduce intensity on lower performance devices
    const intensity = capabilities.isHighPerformance ? 1 : 0.5;
    
    return {
      rotate: baseRotate * intensity,
      translateX: baseTranslateX * intensity,
      translateY: baseTranslateY * intensity,
    };
  };

  const tiltConfig = getTiltConfig();

  // Dedicated tilt config for first item - always tilted on desktop
  const getFirstItemTiltConfig = () => {
    if (!isLargeScreen) {
      return { rotate: 0, translateX: 0, translateY: 0 };
    }
    
    const baseRotate = project.side === "right" ? 6 : -6;
    const baseTranslateX = project.side === "right" ? 15 : -15;
    const baseTranslateY = -10;
    
    return {
      rotate: baseRotate,
      translateX: baseTranslateX,
      translateY: baseTranslateY,
    };
  };

  const firstItemTiltConfig = getFirstItemTiltConfig();

  return (
      <section id={`proj-${project.id}`} className="relative scroll-mt-20 sm:scroll-mt-28 z-10">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:py-16 lg:py-24">
          <div className={`flex flex-col-reverse ${sideClass} items-center gap-8 sm:gap-10 lg:gap-16`}>
            <motion.div
                initial={isFirst ? { 
                  opacity: 1,  // Always visible for first item
                  x: 0,        // Always centered for first item
                  rotate: firstItemTiltConfig.rotate,
                  translateX: firstItemTiltConfig.translateX,
                  translateY: firstItemTiltConfig.translateY
                } : { opacity: 0, x: mediaInitialX }}
                whileInView={!isFirst ? { 
                  opacity: 1, 
                  x: 0,
                  rotate: tiltConfig.rotate,
                  translateX: tiltConfig.translateX,
                  translateY: tiltConfig.translateY
                } : undefined}
                viewport={!isFirst ? { once: true, margin: "-20%" } : undefined}
                transition={!isFirst ? { 
                  duration: animConfig.duration,
                  ease: animConfig.ease,
                  delay: animConfig.enabled ? 0.1 : 0
                } : undefined}
                className={`shrink-0 w-full flex justify-center lg:w-auto contain-layout ${
                  isLargeScreen && animConfig.enabled ? 'cursor-pointer gpu-accelerated' : 'pointer-events-none'
                }`}
                whileHover={isLargeScreen && animConfig.enabled ? { 
                  scale: capabilities.isHighPerformance ? 1.04 : 1.02,
                  rotate: 0, // Return to neutral on hover
                  translateX: 0,
                  translateY: 0,
                  transition: { 
                    duration: animConfig.duration * 0.1,
                    ease: animConfig.ease,
                    type: "tween"
                  }
                } : {}}
                whileTap={isLargeScreen && animConfig.enabled ? {
                  scale: capabilities.isHighPerformance ? 1.02 : 1.01,
                  transition: { duration: 0.05 }
                } : {}}
                onClick={isLargeScreen ? () => openModal(project.id) : undefined}
                title={isLargeScreen ? "Case Study" : undefined}
                style={{
                  willChange: animConfig.enabled ? 'transform' : 'auto',
                  transform: animConfig.enabled ? 'translate3d(0,0,0)' : undefined,
                }}
            >
              <MediaFrame aspectRatio={project.aspectRatio || "landscape"}>
                <MediaImage src={project.src} isFirst={isFirst} />
              </MediaFrame>
            </motion.div>

            <motion.div
                initial={isFirst ? { 
                  opacity: 1,  // Always visible for first item
                  x: 0         // Always centered for first item
                } : { opacity: 0, x: contentInitialX }}
                whileInView={!isFirst ? { 
                  opacity: 1, 
                  x: 0
                } : undefined}
                viewport={!isFirst ? { once: true, margin: "-15%" } : undefined}
                transition={!isFirst ? { 
                  duration: animConfig.duration, 
                  ease: animConfig.ease,
                  delay: animConfig.enabled ? 0.1 : 0
                } : undefined}
                className={`flex-1 w-full text-center lg:text-left ${TEXT} contain-style`}
                whileHover={animConfig.enabled ? { 
                  scale: isLargeScreen && capabilities.isHighPerformance ? 1.02 : 1,
                  transition: { duration: animConfig.duration * 0.5, ease: "easeOut" }
                } : {}}
                style={{
                  willChange: animConfig.enabled ? 'transform' : 'auto',
                }}
            >
              <Overline>Project</Overline>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mt-2">{project.title}</h3>
              <p className={`${SUB} leading-relaxed max-w-prose mx-auto lg:mx-0 mt-4 text-sm sm:text-base`}>{project.blurb}</p>

              <div className="mt-4 flex flex-wrap gap-2 justify-center lg:justify-start">
                {project.tags?.map((t) => (
                    <span
                        key={t}
                        className="px-3 py-1 rounded-full text-xs border border-blue-300/20 text-blue-200/90 bg-blue-500/5"
                    >
                  {t}
                </span>
                ))}
              </div>

              {/* actions - responsive layout */}
              <div className="mt-6 sm:mt-7 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 relative">
                <CodeButton project={project} />
                {project.links?.demo ? (
                    <a
                        href={project.links.demo}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => analytics.trackExternalLink('demo', project.id)}
                        className="inline-flex items-center gap-2 rounded-full px-4 py-3 sm:py-2 text-sm border border-white/10 hover:bg-white/5 touch-manipulation w-full sm:w-auto justify-center"
                    >
                      Demo
                    </a>
                ) : null}
                <button
                    onClick={() => {
                      analytics.trackProjectView(project.id, 'case_study');
                      openModal(project.id);
                    }}
                    className="inline-flex items-center gap-2 rounded-full px-4 py-3 sm:py-2 text-sm font-medium transition border border-white/10 hover:bg-white/5 touch-manipulation w-full sm:w-auto justify-center"
                >
                  Case Study
                </button>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-4">
          <div className="h-px w-full bg-white/10" />
        </div>
      </section>
  );
}

// — CONTACT —
function ContactSection() {
  const analytics = useAnalytics();
  const [copyStatus, setCopyStatus] = useState<EmailCopyStatus>('idle');

  const copyEmail = async () => {
    const success = await copyToClipboard(buildContactEmail());
    analytics.trackEmailCopy(success);
    setCopyStatus(success ? 'copied' : 'failed');
    window.setTimeout(() => setCopyStatus('idle'), success ? 2000 : 4000);
  };

  return (
      <section className="relative z-10">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:py-16">
          <div className="rounded-xl sm:rounded-2xl border border-white/10 p-6 sm:p-8 bg-gradient-to-br from-blue-500/10 to-transparent">
            <h3 className="text-xl sm:text-2xl font-semibold text-center sm:text-left">Let's build something exceptional.</h3>
            <p className={`${SUB} mt-2 text-center sm:text-left text-sm sm:text-base`}>Reach out for collaborations, job offers, or just to say hi.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 items-center">
              <a
                  href={buildMailtoUrl()}
                  onClick={() => analytics.trackContactInteraction('email_compose_open')}
                  className="w-full sm:w-auto px-6 py-3 sm:py-2 rounded-full border border-white/10 hover:bg-white/5 touch-manipulation flex items-center justify-center gap-2 text-sm font-medium transition-all"
              >
                Email me
              </a>
              <button
                  type="button"
                  onClick={copyEmail}
                  className="w-full sm:w-auto px-6 py-3 sm:py-2 rounded-full border border-white/10 hover:bg-white/5 touch-manipulation flex items-center justify-center gap-2 text-sm font-medium transition-all"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm8 7 8-5H4l8 5Zm-8 7h16V8l-8 5L4 8v10Z" />
                </svg>
                {emailCopyLabel(copyStatus)}
              </button>
              <a 
                href="https://github.com/Niv-Ro" 
                target="_blank"
                rel="noreferrer"
                onClick={() => analytics.trackExternalLink('github')}
                className="w-full sm:w-auto px-6 py-3 sm:py-2 rounded-full border border-white/10 hover:bg-white/5 touch-manipulation flex items-center justify-center gap-2 text-sm font-medium transition-all"
              >
                <GitHubIcon className="h-4 w-4" />
                GitHub
              </a>
              <a 
                href="https://www.linkedin.com/in/niv-romano/" 
                target="_blank"
                rel="noreferrer"
                onClick={() => analytics.trackExternalLink('linkedin')}
                className="w-full sm:w-auto px-6 py-3 sm:py-2 rounded-full border border-white/10 hover:bg-white/5 touch-manipulation flex items-center justify-center gap-2 text-sm font-medium transition-all"
              >
                <LinkedInIcon className="h-4 w-4" />
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>
  );
}

// — SIDEBAR —
function ProjectsSidebar({
                           open,
                           onClose,
                           onSelectProject,
                         }: {
  open: boolean;
  onClose: () => void;
  onSelectProject: (id: string) => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  return (
      <div className={`fixed inset-0 z-[65] ${open ? "" : "pointer-events-none"}`}>
        <div
            className={`absolute inset-0 bg-black/60 transition-opacity ${open ? "opacity-100" : "opacity-0"}`}
            onClick={onClose}
        />
        <aside
            className={`absolute left-0 top-0 h-full w-[320px] max-w-[85vw] bg-gradient-to-br from-blue-500/10 to-transparent border-r border-white/10 shadow-2xl transform transition-transform backdrop-blur-md ${
                open ? "translate-x-0" : "-translate-x-full"
            }`}
        >
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <span className="text-sm uppercase tracking-widest text-blue-200/80">Projects</span>
            <button 
              onClick={onClose} 
              className="px-3 py-2 rounded-full border border-white/10 hover:bg-white/5 text-sm touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Close projects menu"
            >
              ✕
            </button>
          </div>
          <div className="overflow-auto h-[calc(100%-73px)] overscroll-contain">
            <ul className="p-3 space-y-3 pb-6">
              {PROJECTS.map((p, index) => (
                  <motion.li 
                      key={p.id} 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="rounded-xl border border-white/20 hover:border-blue-300/40 transition-all duration-300 bg-gradient-to-br from-white/5 to-transparent hover:from-blue-500/10 hover:to-blue-400/5 hover:shadow-lg hover:shadow-blue-500/10"
                  >
                    <button
                        onClick={() => {
                          onSelectProject(p.id);
                          onClose();
                        }}
                        className="w-full text-left p-4 rounded-xl hover:scale-[1.01] sm:hover:scale-[1.02] transition-transform duration-200 touch-manipulation min-h-[60px]"
                    >
                      <div className="font-medium text-white/90 hover:text-white transition-colors text-sm sm:text-base leading-relaxed">{p.title}</div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {p.tags.map((t) => (
                            <span
                                key={t}
                                className="px-2 py-1 rounded-full text-[10px] sm:text-[11px] border border-blue-300/30 text-blue-200 bg-blue-500/10 hover:bg-blue-400/15 transition-colors"
                            >
                        {t}
                      </span>
                        ))}
                      </div>
                    </button>
                  </motion.li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
  );
}

// — PORTFOLIO PAGE —
export default function Portfolio() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(getParam("project"));
  const analytics = useAnalytics();
  
  // Preload first few project images for better initial loading
  useEffect(() => {
    const preloadImages = async () => {
      if (PROJECTS.length > 0 && typeof window !== "undefined") {
        const isMobileDevice = window.innerWidth < 768;

        // Always preload first image
        const firstImage = new Image();
        firstImage.crossOrigin = "anonymous"; // Help with CORS if needed
        firstImage.src = PROJECTS[0].src;

        // On desktop, also preload second image with a delay
        if (!isMobileDevice && PROJECTS.length > 1) {
          setTimeout(() => {
            const secondImage = new Image();
            secondImage.crossOrigin = "anonymous";
            secondImage.src = PROJECTS[1].src;
          }, 100); // Small delay to not compete with first image
        }
      }
    };

    preloadImages();

    // Track initial page view
    trackPageView(window.location.pathname, document.title);

    // Track performance metrics on load
    setTimeout(() => {
      trackPerformanceMetrics();
    }, 1000);
  }, []);

  useEffect(() => {
    if (isSandboxedUrl()) return;
    const onPop = () => setActiveProjectId(getParam("project"));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const openModal = (id: string) => {
    setActiveProjectId(id);
    setParam("project", id);
    analytics.trackCaseStudyOpen(id);
  };
  const closeModal = () => {
    if (activeProjectId) {
      analytics.trackCaseStudyClose(activeProjectId);
    }
    setActiveProjectId(null);
    setParam("project", null);
  };
  const projIndex = PROJECTS.findIndex((p) => p.id === activeProjectId);
  const nextProj = () => {
    if (projIndex < 0) return;
    const n = (projIndex + 1) % PROJECTS.length;
    analytics.trackModalNavigation('next', activeProjectId || undefined);
    openModal(PROJECTS[n].id);
  };
  const prevProj = () => {
    if (projIndex < 0) return;
    const n = (projIndex - 1 + PROJECTS.length) % PROJECTS.length;
    analytics.trackModalNavigation('prev', activeProjectId || undefined);
    openModal(PROJECTS[n].id);
  };
  const activeProject = PROJECTS.find((p) => p.id === activeProjectId);

  return (
      <div className={`${BG} min-h-screen ${TEXT} antialiased`}>
        <Background />
        <Header onOpenSidebar={() => {
          setSidebarOpen(true);
          analytics.trackSidebarToggle('open');
        }} />

        {/* HERO */}
        <section className="relative z-10">
          <div className="mx-auto max-w-6xl px-4 pt-16 sm:pt-20 lg:pt-24 pb-6 sm:pb-8">
            <div className="text-center sm:text-left">
              <motion.p
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="text-xs sm:text-sm tracking-widest uppercase text-blue-200/80 mb-4"
              >
                Portfolio
              </motion.p>
            </div>
            <motion.h2
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut", delay: 0.05 }}
                className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-center sm:text-left leading-tight"
            >
              Crafted software with precision
            </motion.h2>
            <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.12 }}
                className={`${SUB} mt-4 max-w-2xl text-center sm:text-left text-sm sm:text-base leading-relaxed mx-auto sm:mx-0`}
            >
              Clean interfaces, measured motion, and robust code across Python, Swift, and the Web.
            </motion.p>
          </div>
        </section>

        {/* PROJECTS */}
        {PROJECTS.map((p, index) => (
            <ProjectSection key={p.id} project={p} openModal={openModal} isFirst={index === 0} />
        ))}

        {/* CONTACT */}
        <ContactSection />

        {/* CASE STUDY */}
        <CaseStudyModal project={activeProject} onClose={closeModal} onPrev={prevProj} onNext={nextProj} />

        {/* FOOTER */}
        <footer className="mx-auto max-w-6xl px-4 py-16 z-10 relative">
          <div className="flex items-center justify-center text-sm text-zinc-400">
            <span>© {new Date().getFullYear()} Niv Romano. All rights reserved.</span>
          </div>
        </footer>

        {/* SIDEBAR */}
        <ProjectsSidebar
            open={sidebarOpen}
            onClose={() => {
              setSidebarOpen(false);
              analytics.trackSidebarToggle('close');
            }}
            onSelectProject={(id) => {
              analytics.trackProjectView(id, 'preview');
              openModal(id);
            }}
        />
      </div>
  );
}
